/**
 * 認証コンテキスト
 *
 * @file AuthContext.tsx
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * アプリケーション全体での認証状態管理。ログイン、ログアウト、
 * ユーザー情報取得、ロール判定（管理者、販売者、購入者）の機能を提供。
 * Fast Refresh 互換のため AuthProvider コンポーネントのみを export する。
 * フック（useAuth 等）は useAuth.ts から import すること。
 */
import React, { useReducer, useEffect, useCallback, useMemo } from 'react';
import { authService } from '@/services/api/auth.service';
import { User, AdminUser, UserRole, RegisterRequest, LoginResponse, AdminLoginOtpRequiredResponse, SellerLoginOtpRequiredResponse } from '@/types';
import { AuthContext, AuthContextType } from './useAuth';

interface AuthState {
  user: User | AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: User | AdminUser }
  | { type: 'AUTH_REGISTER_SUCCESS' }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_FORCE_PASSWORD_CHANGE_FALSE' };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  console.log('[AuthContext reducer] action:', action.type, 'current state:', state);
  switch (action.type) {
    case 'AUTH_START': {
      const startState = {
        ...state,
        isLoading: true,
        error: null
      };
      console.log('[AuthContext reducer] AUTH_START -> new state:', startState);
      return startState;
    }
    case 'AUTH_SUCCESS': {
      if (!action.payload) {
        console.error('[AuthContext reducer] AUTH_SUCCESS current state:', state.isLoading ? 'loading' : 'idle', 'new state: {user: null}');
        throw new Error('AUTH_SUCCESS requires valid user payload');
      }
      const successState = {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null
      };
      console.log('[AuthContext reducer] AUTH_SUCCESS -> new state:', successState);
      return successState;
    }
    case 'AUTH_REGISTER_SUCCESS': {
      const registerSuccessState = {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      };
      console.log('[AuthContext reducer] AUTH_REGISTER_SUCCESS -> new state:', registerSuccessState);
      return registerSuccessState;
    }
    case 'AUTH_ERROR': {
      const errorState = {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload
      };
      console.log('[AuthContext reducer] AUTH_ERROR -> new state:', errorState);
      return errorState;
    }
    case 'AUTH_LOGOUT': {
      const logoutState = {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      };
      console.log('[AuthContext reducer] AUTH_LOGOUT -> new state:', logoutState);
      return logoutState;
    }
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };
    case 'SET_FORCE_PASSWORD_CHANGE_FALSE': {
      if (!state.user) {
        return state;
      }
      return {
        ...state,
        user: { ...state.user, forcePasswordChange: false }
      };
    }
    default:
      return state;
  }
};

// 実API統合により開発環境でも実認証を使用
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true, // アプリ起動時の認証チェック中はtrue
  error: null
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  /**
   * APIエラーレスポンスからエラーコードを取得
   * X-Error-Codeヘッダーが存在すればそれを返す
   * 翻訳は各コンポーネント側で行う
   */
  const getErrorCode = (error: unknown): string | null => {
    // AxiosErrorの場合、レスポンスヘッダーからエラーコードを取得
    // Axiosはヘッダーを小文字に正規化するため、小文字でアクセス
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { headers?: Record<string, string> } };
      const errorCode = axiosError.response?.headers?.['x-error-code'] ||
                       axiosError.response?.headers?.['X-Error-Code'];
      return errorCode || null;
    }
    return null;
  };

  // アプリ起動時にhttpOnly Cookie経由で認証状態を確認
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const checkAuth = async () => {
      try {
        const currentPath = window.location.pathname;

        // ログイン画面では認証APIを呼ばない
        const isLoginPage = currentPath === '/login';

        if (isLoginPage) {
          if (isMounted) dispatch({ type: 'AUTH_ERROR', payload: '' });
          return;
        }

        if (isMounted) dispatch({ type: 'AUTH_START' });

        // API経由で認証状態を確認（httpOnly Cookieが自動送信される）
        const isAdmin = currentPath.startsWith('/admin');
        const isSeller = currentPath.startsWith('/seller');

        let user: User | AdminUser | null = null;

        // フォールバック完全禁止 - 各ロールは専用エンドポイントのみ使用
        if (isAdmin) {
          user = await authService.getCurrentAdminUser(controller.signal);
        } else if (isSeller) {
          user = await authService.getCurrentSellerUser(controller.signal);
        } else {
          user = await authService.getCurrentUser(controller.signal);
        }

        if (!user) {
          throw new Error('ユーザー情報の取得に失敗しました。');
        }

        if (isMounted) dispatch({ type: 'AUTH_SUCCESS', payload: user });
      } catch (error) {
        if (!isMounted) return;
        const axiosError = error as { code?: string; name?: string };
        if (axiosError.code === 'ERR_CANCELED' || axiosError.name === 'CanceledError') return;
        // 認証失敗時（Cookieが存在しない、または期限切れ）
        // Fail-Fast原則: 即座に失敗させる
        dispatch({ type: 'AUTH_ERROR', payload: 'AUTH_CHECK_FAILED' });
      }
    };

    checkAuth();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  const loginBuyer = useCallback(async (email: string, password: string, remember?: boolean) => {
    try {
      dispatch({ type: 'AUTH_START' });

      const response = await authService.loginBuyer({ email, password, remember });

      // トークンはサービス内で既に保存済み
      dispatch({ type: 'AUTH_SUCCESS', payload: response.user });
    } catch (error) {
      const errorCode = getErrorCode(error);
      const errorMessage = errorCode || 'LOGIN_FAILED';
      dispatch({
        type: 'AUTH_ERROR',
        payload: errorMessage
      });
      throw error;
    }
  }, []);

  const loginSeller = useCallback(async (email: string, password: string, remember?: boolean): Promise<SellerLoginOtpRequiredResponse | void> => {
    try {
      dispatch({ type: 'AUTH_START' });

      const response = await authService.loginSeller({ email, password, remember });

      if ('requiresTwoFa' in response && response.requiresTwoFa === true) {
        dispatch({ type: 'AUTH_ERROR', payload: '' });
        return response as unknown as SellerLoginOtpRequiredResponse;
      }

      const sellerResponse = response as import('@/types').SellerLoginResponse;

      if (!sellerResponse.seller?.id || !sellerResponse.seller?.email) {
        throw new Error('Seller情報の取得に失敗しました。必須項目が欠けています。');
      }

      const sellerUser: User = {
        id: sellerResponse.seller.id,
        email: sellerResponse.seller.email,
        firstName: (typeof sellerResponse.seller.businessName === 'object' ? sellerResponse.seller.businessName?.ja : sellerResponse.seller.businessName) || '',
        lastName: '',
        role: UserRole.SELLER,
        language: 'ja',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      dispatch({ type: 'AUTH_SUCCESS', payload: sellerUser });
    } catch (error) {
      const errorCode = getErrorCode(error);
      const errorMessage = errorCode || 'LOGIN_FAILED';
      dispatch({
        type: 'AUTH_ERROR',
        payload: errorMessage
      });
      throw error;
    }
  }, []);

  const loginAdmin = useCallback(async (email: string, password: string): Promise<AdminLoginOtpRequiredResponse | void> => {
    try {
      dispatch({ type: 'AUTH_START' });

      const result = await authService.adminLogin({ email, password });

      // 2FA要求フェーズではAUTH_SUCCESSを発行しない
      if ('requiresTwoFa' in result) {
        // ローディング状態を解除し、OTP要求レスポンスを呼び出し元に返す
        dispatch({ type: 'AUTH_ERROR', payload: '' });
        return result as unknown as AdminLoginOtpRequiredResponse;
      }

      // AdminLoginResponseが確定
      const adminResponse = result as import('@/types').AdminLoginResponse;
      dispatch({ type: 'AUTH_SUCCESS', payload: adminResponse.user });
    } catch (error) {
      const errorCode = getErrorCode(error);
      const errorMessage = errorCode || 'LOGIN_FAILED';
      dispatch({
        type: 'AUTH_ERROR',
        payload: errorMessage
      });
      throw error;
    }
  }, []);

  const verifyAdminOtp = useCallback(async (email: string, password: string, otpCode: string) => {
    try {
      dispatch({ type: 'AUTH_START' });

      const result = await authService.adminLogin({ email, password, twofaToken: otpCode });

      // OTP送信後に再度2FA要求が返った場合は想定外エラー
      if ('requiresTwoFa' in result && (result as { requiresTwoFa: boolean }).requiresTwoFa) {
        throw new Error('Unexpected requires_2fa response after OTP submission');
      }

      // OTP認証成功 → AUTH_SUCCESS を発火
      const adminResponse = result as import('@/types').AdminLoginResponse;
      dispatch({ type: 'AUTH_SUCCESS', payload: adminResponse.user });
    } catch (error) {
      const errorCode = getErrorCode(error);
      const errorMessage = errorCode || 'OTP_VERIFICATION_FAILED';
      dispatch({
        type: 'AUTH_ERROR',
        payload: errorMessage
      });
      throw error;
    }
  }, []);

  const verifySellerOtp = useCallback(async (email: string, password: string, otpCode: string) => {
    try {
      dispatch({ type: 'AUTH_START' });

      const result = await authService.loginSeller({ email, password, twofaToken: otpCode });

      if ('requiresTwoFa' in result && (result as { requiresTwoFa: boolean }).requiresTwoFa) {
        throw new Error('Unexpected requires_2fa response after OTP submission');
      }

      const sellerResponse = result as import('@/types').SellerLoginResponse;

      if (!sellerResponse.seller?.id || !sellerResponse.seller?.email) {
        throw new Error('Seller情報の取得に失敗しました。必須項目が欠けています。');
      }

      const sellerUser: User = {
        id: sellerResponse.seller.id,
        email: sellerResponse.seller.email,
        firstName: (typeof sellerResponse.seller.businessName === 'object' ? sellerResponse.seller.businessName?.ja : sellerResponse.seller.businessName) || '',
        lastName: '',
        role: UserRole.SELLER,
        language: 'ja',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      dispatch({ type: 'AUTH_SUCCESS', payload: sellerUser });
    } catch (error) {
      const errorCode = getErrorCode(error);
      const errorMessage = errorCode || 'OTP_VERIFICATION_FAILED';
      dispatch({
        type: 'AUTH_ERROR',
        payload: errorMessage
      });
      throw error;
    }
  }, []);

  const loginWithGoogle = useCallback(async (credential: string) => {
    try {
      dispatch({ type: 'AUTH_START' });

      const response = await (authService as typeof authService & { loginWithGoogle: (credential: string) => Promise<LoginResponse & { isNewUser?: boolean }> }).loginWithGoogle(credential);

      dispatch({ type: 'AUTH_SUCCESS', payload: response.user });
    } catch (error) {
      const errorCode = getErrorCode(error);
      const errorMessage = errorCode || 'GOOGLE_LOGIN_FAILED';
      dispatch({
        type: 'AUTH_ERROR',
        payload: errorMessage
      });
      throw error;
    }
  }, []);

  const login = useCallback(async (email: string, password: string, role: 'buyer' | 'seller' | 'admin', remember?: boolean): Promise<AdminLoginOtpRequiredResponse | void> => {
    if (!role) {
      throw new Error('[AuthContext] login: role parameter is required');
    }
    switch (role) {
      case 'buyer':
        return loginBuyer(email, password, remember);
      case 'seller':
        return loginSeller(email, password, remember);
      case 'admin':
        return loginAdmin(email, password);
      default:
        throw new Error(`無効な認証ロール: ${role}`);
    }
  }, [loginBuyer, loginSeller, loginAdmin]);

  const register = useCallback(async (data: RegisterRequest) => {
    try {
      dispatch({ type: 'AUTH_START' });

      await authService.registerBuyer(data);

      // 会員登録成功後、即座にログイン状態にしない
      // メール確認完了後にログインしてもらうため、AUTH_SUCCESSはディスパッチしない
      // isLoadingをfalseにするため、AUTH_REGISTER_SUCCESSをディスパッチ
      dispatch({ type: 'AUTH_REGISTER_SUCCESS' });
    } catch (error) {
      const errorCode = getErrorCode(error);
      const errorMessage = errorCode || 'REGISTRATION_FAILED';
      dispatch({
        type: 'AUTH_ERROR',
        payload: errorMessage
      });
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
      dispatch({ type: 'AUTH_LOGOUT' });
    } catch (error) {
      console.error('ログアウトエラー:', error);
      // ローカルの状態はクリアする
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const setForcePasswordChangeFalse = useCallback(() => {
    dispatch({ type: 'SET_FORCE_PASSWORD_CHANGE_FALSE' });
  }, []);

  const value: AuthContextType = useMemo(() => ({
    ...state,
    login,
    loginBuyer,
    loginSeller,
    loginAdmin,
    verifyAdminOtp,
    verifySellerOtp,
    loginWithGoogle,
    register,
    logout,
    clearError,
    setForcePasswordChangeFalse
  }), [state, login, loginBuyer, loginSeller, loginAdmin, verifyAdminOtp, verifySellerOtp, loginWithGoogle, register, logout, clearError, setForcePasswordChangeFalse]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
